import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  VStack,
  Divider,
  Text,
  Flex,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  useToast,
} from '@chakra-ui/react';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useNavigate } from 'react-router-dom';
import { createPaymentIntent, saveOrder } from '../utils/stripeUtils';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';

/**
 * StripePaymentForm handles the payment process using Stripe Elements.
 * 
 * @param {Object} props
 * @param {number} props.amount - Total amount to charge in cents
 * @param {Function} props.onPaymentSuccess - Callback on successful payment
 * @param {Function} props.onPaymentError - Callback on payment error
 */
const StripePaymentForm = ({ 
  amount, 
  onPaymentSuccess,
  onPaymentError,
  cardName
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useAuth();
  const { cartItems, cartTotal, emptyCart, saveCartForCheckout } = useCart();
  
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [succeeded, setSucceeded] = useState(false);

  // Add this helper function to get cart data from localStorage if needed
  const getLocalCartItems = () => {
    try {
      const storedCart = localStorage.getItem('playmaker_cart');
      if (storedCart) {
        const parsedCart = JSON.parse(storedCart);
        if (parsedCart && parsedCart.items && Array.isArray(parsedCart.items)) {
          console.log('Retrieved cart items from localStorage:', parsedCart.items.length);
          return parsedCart.items;
        }
      }
      return [];
    } catch (error) {
      console.error('Error retrieving cart from localStorage:', error);
      return [];
    }
  };

  useEffect(() => {
    // Make sure cart data is available when component mounts
    if (cartItems && cartItems.length > 0) {
      console.log('StripePaymentForm: Cart data available:', {
        items: cartItems.length,
        total: cartTotal
      });
      
      // Save cart data to localStorage for persistence
      try {
        saveCartForCheckout();
      } catch (error) {
        console.error('Error saving cart for checkout:', error);
      }
    } else {
      // If cart is empty, try to retrieve from localStorage
      const storedCart = localStorage.getItem('playmaker_cart');
      if (storedCart) {
        console.log('StripePaymentForm: No cart data in context, but found in localStorage');
      } else {
        console.warn('StripePaymentForm: No cart data available in context or localStorage');
      }
    }
  }, [cartItems, cartTotal, saveCartForCheckout]);

  const handleSubmit = async (event) => {
    if (event && event.preventDefault) {
      event.preventDefault();
    }
    
    if (!stripe || !elements) {
      setError("Stripe hasn't loaded yet. Please try again in a moment.");
      return;
    }
    
    // Ensure we have cart items
    const effectiveItems = cartItems && cartItems.length > 0 ? cartItems : 
                          getLocalCartItems();
    
    if (!effectiveItems || effectiveItems.length === 0) {
      setError("No items in cart. Please add items before proceeding to payment.");
      setProcessing(false);
      return;
    }
    
    // Reset error state
    setError(null);
    setProcessing(true);
    
    console.log('Processing payment for amount:', amount / 100);
    
    try {
      console.log('Creating payment intent...');
      // Create the payment intent
      const paymentIntentResult = await createPaymentIntent(
        amount / 100, 
        effectiveItems
      );
      
      if (paymentIntentResult.error) {
        console.error('Error creating payment intent:', paymentIntentResult.error);
        throw new Error(paymentIntentResult.error);
      }
      
      if (!paymentIntentResult.clientSecret) {
        console.error('No client secret returned from payment intent creation');
        throw new Error('Payment initialization failed: No client secret received');
      }
      
      // Log the client secret format (but mask most of it)
      const secretFormat = paymentIntentResult.clientSecret.replace(/^(pi_[^_]+)_secret_(.{3}).*$/, '$1_secret_$2***');
      console.log('Confirming payment with client secret format:', secretFormat);
      
      // Confirm card payment
      console.log('Calling stripe.confirmCardPayment...');
      const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(
        paymentIntentResult.clientSecret,
        {
          payment_method: {
            card: elements.getElement(CardElement),
            billing_details: {
              name: cardName || (user ? `${user.user_metadata?.given_name || ''} ${user.user_metadata?.family_name || ''}` : ''),
              email: user?.email || '',
            },
          },
        }
      );
      
      console.log('Payment confirmation response received');
      
      if (confirmError) {
        console.error('Payment confirmation error:', confirmError);
        throw new Error(confirmError.message);
      }
      
      if (paymentIntent.status === 'succeeded') {
        // Payment successful
        setSucceeded(true);
        console.log('Payment successful!', paymentIntent);
        
        // Save order data for success page
        const orderData = {
          id: paymentIntent.id,
          amount: amount / 100, // Convert cents back to dollars
          status: paymentIntent.status,
          payment_intent_id: paymentIntent.id,
          created_at: new Date().toISOString(),
          items: effectiveItems.map(item => ({
            id: item.id,
            description: item.name || 'Custom Design',
            price: item.price || (amount / 100 / effectiveItems.length), // if no individual price, divide total
            type: item.requires_shipping ? 'physical' : 'digital',
            image_url: item.url || item.watermarkedUrl || item.display_url || item.image_url,
            shipped: false
          }))
        };
        
        console.log('Saving order data:', orderData);
        
        // Call saveOrder function from stripeUtils
        try {
          await saveOrder(orderData);
          console.log('Order saved successfully');
        } catch (saveError) {
          console.warn('Error saving order details:', saveError);
          // Continue even if save fails, as we have the order data to show
        }
        
        // Empty the cart - use a try/catch in case the cart context is unavailable
        try {
          if (typeof emptyCart === 'function') {
            emptyCart();
            console.log('Cart emptied after successful payment');
          }
        } catch (cartError) {
          console.warn('Error emptying cart:', cartError);
        }
        
        // Prepare data with item IDs for marking as purchased
        const itemIds = effectiveItems.map(item => item.id).filter(Boolean);
        
        // Call success callback if provided
        if (onPaymentSuccess) {
          console.log('Calling payment success callback');
          onPaymentSuccess(paymentIntent);
        } else {
          // If no callback is provided, navigate directly
          console.log('No payment success callback provided, navigating directly');
          // Clear localStorage cart data
          localStorage.removeItem('playmaker_cart');
          
          // Navigate to success page with order details
          navigate('/order-success', { 
            state: { orderData },
            search: `?order_success=true&items=${itemIds.join(',')}`
          });
        }
        
        toast({
          title: 'Payment successful!',
          description: 'Your order has been processed.',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (err) {
      console.error('Payment error:', err);
      setError(err.message || 'An error occurred with your payment. Please try again.');
      
      if (onPaymentError) {
        onPaymentError(err);
      }
      
      toast({
        title: 'Payment failed',
        description: err.message || 'An error occurred with your payment. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div style={{ width: '100%' }}>
      <VStack spacing={4} align="stretch">
        {error && (
          <Alert status="error">
            <AlertIcon />
            {error}
          </Alert>
        )}
        
        <Box border="1px solid" borderColor="gray.200" borderRadius="md" p={4}>
          <CardElement options={{
            style: {
              base: {
                fontSize: '16px',
                color: '#424770',
                '::placeholder': {
                  color: '#aab7c4',
                },
              },
              invalid: {
                color: '#9e2146',
              },
            },
          }} />
        </Box>
        
        <Button
          onClick={handleSubmit}
          colorScheme="blue"
          isLoading={processing}
          loadingText="Processing"
          isDisabled={!stripe || processing || succeeded || (!cartItems?.length && !getLocalCartItems().length)}
          w="100%"
        >
          Pay ${(amount / 100).toFixed(2)}
        </Button>
      </VStack>
    </div>
  );
};

export default StripePaymentForm; 